import { create } from "zustand";
import type {
  BulkImportRole,
  BulkImportSnapshot,
  BulkImportStatus,
} from "@/types/zoteroBulkImport";

interface State {
  jobId: string | null;
  status: BulkImportStatus | "idle";
  errorReason: string | null;
  fetched: number;
  added: number;
  reusedEvents: number;
  duplicates: number;
  failed: number;
  lastFailureReason: string | null;
  lastFailureItemKey: string | null;
  skippedNotPdf: number;
  skippedLinkedFile: number;
  skippedNoParent: number;
  skippedTerminalEvent: number;
  collectionKey: string | null;
  collectionName: string | null;
  conceptId: string | null;
  conceptName: string | null;
  role: BulkImportRole;
}

interface Actions {
  setJobId: (id: string | null) => void;
  setMeta: (m: {
    collectionKey: string | null;
    collectionName: string | null;
    conceptId: string;
    conceptName: string;
    role: BulkImportRole;
  }) => void;
  mergeProgress: (snapshot: BulkImportSnapshot) => void;
  complete: (status: BulkImportStatus, errorReason?: string | null) => void;
  hydrateFromSnapshot: (snapshot: BulkImportSnapshot) => void;
  reset: () => void;
}

const initial: State = {
  jobId: null,
  status: "idle",
  errorReason: null,
  fetched: 0,
  added: 0,
  reusedEvents: 0,
  duplicates: 0,
  failed: 0,
  lastFailureReason: null,
  lastFailureItemKey: null,
  skippedNotPdf: 0,
  skippedLinkedFile: 0,
  skippedNoParent: 0,
  skippedTerminalEvent: 0,
  collectionKey: null,
  collectionName: null,
  conceptId: null,
  conceptName: null,
  role: "trawled",
};

export const useZoteroBulkImportStore = create<State & Actions>((set) => ({
  ...initial,
  setJobId: (id) => set({ jobId: id, status: id ? "pending" : "idle" }),
  setMeta: (m) => set({ ...m }),
  mergeProgress: (s) =>
    set({
      jobId: s.job_id,
      status: s.status,
      errorReason: s.error_reason,
      fetched: s.fetched,
      added: s.added,
      reusedEvents: s.reused_events,
      duplicates: s.duplicates,
      failed: s.failed,
      lastFailureReason: s.last_failure_reason ?? null,
      lastFailureItemKey: s.last_failure_item_key ?? null,
      skippedNotPdf: s.skipped_not_pdf ?? 0,
      skippedLinkedFile: s.skipped_linked_file ?? 0,
      skippedNoParent: s.skipped_no_parent ?? 0,
      skippedTerminalEvent: s.skipped_terminal_event ?? 0,
      collectionKey: s.collection_key,
      conceptId: s.concept_id,
      role: s.role,
    }),
  complete: (status, errorReason = null) =>
    set({ status, errorReason }),
  hydrateFromSnapshot: (s) =>
    set({
      jobId: s.job_id,
      status: s.status,
      errorReason: s.error_reason,
      fetched: s.fetched,
      added: s.added,
      reusedEvents: s.reused_events,
      duplicates: s.duplicates,
      failed: s.failed,
      lastFailureReason: s.last_failure_reason ?? null,
      lastFailureItemKey: s.last_failure_item_key ?? null,
      skippedNotPdf: s.skipped_not_pdf ?? 0,
      skippedLinkedFile: s.skipped_linked_file ?? 0,
      skippedNoParent: s.skipped_no_parent ?? 0,
      skippedTerminalEvent: s.skipped_terminal_event ?? 0,
      collectionKey: s.collection_key,
      conceptId: s.concept_id,
      role: s.role,
    }),
  reset: () => set(initial),
}));
